import React, { useEffect, useState } from "react";
import { Calendar } from "primereact/calendar";
import "./RangeCalendar.css";
import { useTranslation } from "../../contexts/TranslationContext";
import {
  getPrimeReactDateFormat,
  getPrimeReactLocale,
  syncPrimeReactLocale,
} from "../../utils/primeReactLocale";

const RangeCalendar = ({ value, onChange, minDate, maxDate }) => {
  const [internalValue, setInternalValue] = useState(null);
  const [error, setError] = useState(null);
  const { t, currentLanguage } = useTranslation();
  const calendarLocale = getPrimeReactLocale(currentLanguage);
  const calendarDirection = "ltr";
  const calendarDateFormat = getPrimeReactDateFormat(currentLanguage);
  const displayDateLocale = calendarLocale === "ar" ? "ar-EG" : "en-US";

  const minDateObj = minDate ? new Date(minDate) : null;
  const maxDateObj = maxDate ? new Date(maxDate) : null;

  useEffect(() => {
    syncPrimeReactLocale(currentLanguage);
  }, [currentLanguage]);

  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getText = (key, fallback) => {
    const translated = t(key);
    return translated === key ? fallback : translated;
  };

  const handleDateChange = (e) => {
    const selectedDates = e.value;
    setInternalValue(selectedDates);
    setError(null);

    const emitChange = (nextValue) => {
      if (typeof onChange === "function") {
        onChange(nextValue);
      }
    };

    if (selectedDates?.[0] && selectedDates?.[1]) {
      const start = selectedDates[0];
      const end = selectedDates[1];

      if (minDateObj && start < minDateObj) {
        setError(
          `${getText(
            "EarliestAvailableDate",
            calendarLocale === "ar" ? "أقرب تاريخ متاح هو" : "Earliest available date is"
          )} ${minDateObj.toLocaleDateString(displayDateLocale)}`
        );
        return;
      }

      if (maxDateObj && end > maxDateObj) {
        setError(
          `${getText(
            "LatestAvailableDate",
            calendarLocale === "ar" ? "آخر تاريخ متاح هو" : "Latest available date is"
          )} ${maxDateObj.toLocaleDateString(displayDateLocale)}`
        );
        return;
      }

      emitChange({
        startDate: formatDateLocal(start),
        endDate: formatDateLocal(end),
      });
    } else if (!selectedDates?.[0] && !selectedDates?.[1]) {
      emitChange({ startDate: null, endDate: null });
    }
  };

  useEffect(() => {
    if (value?.startDate && value?.endDate) {
      const start = new Date(value.startDate);
      const end = new Date(value.endDate);
      setInternalValue([start, end]);
    } else {
      setInternalValue(null);
    }
  }, [value]);

  return (
    <div className="range-calendar-container">
      <Calendar
        locale={calendarLocale}
        dir={calendarDirection}
        value={internalValue}
        onChange={handleDateChange}
        selectionMode="range"
        numberOfMonths={2}
        hideOnRangeSelection
        readOnlyInput
        placeholder={t("SelectDateRange")}
        dateFormat={calendarDateFormat}
        className="range-calendar-input"
        showButtonBar
        onClearButtonClick={(e) => handleDateChange(e)}
        minDate={minDateObj}
        maxDate={maxDateObj}
      />
      {error && <div className="calendar-error">{error}</div>}
    </div>
  );
};

export default RangeCalendar;
